/**
 * @cafe-os/db — конструктор тарифов (миграция 00458).
 *
 * Кафе (owner/manager):
 *   • getTariffGrid         — сетка цен {key: price} (anon-readable, лендинг тоже)
 *   • requestTariffChange   — отправить собранную конфигурацию на подтверждение
 *   • getMyTariffRequest    — последний запрос тенанта (pending в приоритете)
 *   • cancelMyTariffRequest — отозвать pending
 *
 * Супер-админ:
 *   • superAdminListTariffRequests / superAdminApplyTariffRequest /
 *     superAdminRejectTariffRequest / superAdminSetTariffPrice
 *
 * Расчёт суммы — packages/utils (computeTariff); сервер считает сам
 * (tariff_config_price) и клиентской цене не верит.
 */
import { callRpcWithDefaults } from "./errors";

export type TariffRequestStatus = "pending" | "applied" | "rejected" | "cancelled";

/** {hall: 0..4, staff: 0..3, delivery, ai, wl} — см. TariffConfig в @cafe-os/utils. */
export type TariffConfigJson = {
  hall: number;
  staff: number;
  delivery: boolean;
  ai: boolean;
  wl: boolean;
};

export interface TariffRequestRow {
  id: string;
  tenant_id: string;
  config: TariffConfigJson;
  features: Record<string, unknown>;
  monthly_price: number;
  currency: string;
  status: TariffRequestStatus;
  note: string | null;
  requested_by: string | null;
  decided_note: string | null;
  decided_at: string | null;
  created_at: string;
}

/** Строка списка супер-админа: запрос + имя кафе + текущий план. */
export interface TariffRequestAdminRow extends TariffRequestRow {
  tenant_name: Record<string, string> | null;
  current_plan_id: string | null;
  current_price: number | null;
}

export async function getTariffGrid(): Promise<Record<string, number>> {
  const data = await callRpcWithDefaults<Record<string, number>>("get_tariff_grid", {});
  return data && typeof data === "object" ? data : {};
}

export async function requestTariffChange(
  config: TariffConfigJson,
  note?: string,
): Promise<TariffRequestRow | null> {
  return callRpcWithDefaults<TariffRequestRow>("request_tariff_change", {
    p_config: config,
    p_note: note ?? null,
  });
}

export async function getMyTariffRequest(): Promise<TariffRequestRow | null> {
  return callRpcWithDefaults<TariffRequestRow | null>("get_my_tariff_request", {});
}

export async function cancelMyTariffRequest(): Promise<boolean> {
  const data = await callRpcWithDefaults<boolean>("cancel_my_tariff_request", {});
  return data === true;
}

export async function superAdminListTariffRequests(
  status: TariffRequestStatus | null = "pending",
  limit = 50,
): Promise<TariffRequestAdminRow[]> {
  const data = await callRpcWithDefaults<TariffRequestAdminRow[]>("super_admin_list_tariff_requests", {
    p_status: status,
    p_limit: limit,
  });
  return Array.isArray(data) ? data : [];
}

export async function superAdminApplyTariffRequest(id: string, reason?: string): Promise<TariffRequestRow | null> {
  return callRpcWithDefaults<TariffRequestRow>("super_admin_apply_tariff_request", {
    p_id: id,
    p_reason: reason ?? null,
  });
}

export async function superAdminRejectTariffRequest(id: string, note?: string): Promise<TariffRequestRow | null> {
  return callRpcWithDefaults<TariffRequestRow>("super_admin_reject_tariff_request", {
    p_id: id,
    p_note: note ?? null,
  });
}

export async function superAdminSetTariffPrice(key: string, price: number): Promise<Record<string, number>> {
  const data = await callRpcWithDefaults<Record<string, number>>("super_admin_set_tariff_price", {
    p_key: key,
    p_price: price,
  });
  return data && typeof data === "object" ? data : {};
}
